import { useEffect, useMemo } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { useGetCustomMapsInfiniteQuery } from "api/topicsApi.slice";
import { ChevronIcon, InfinityLoader, SectionTitleUnderlined, StyledGrid } from "common/components";
import { breakpoints, linkStyles, mediaStyles } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { CreateNewMapSection } from "domains/create/landing/CreateNewMapSection";
import { TopicSection } from "domains/discover/components/TopicSection";
import { getSiteLanguage } from "domains/language/selectors";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import { getCanAccessCreate } from "entities/permissions/selectors";
import { fetchSharedWithMe } from "entities/topics/actions";
import { createTopicsSelector, getTopicsSharedWithMeIds } from "entities/topics/selectors";

const StyledCarouselContainer = styled.div`
  ${mediaStyles(({ spacing }) => `margin: 2rem ${spacing}rem 0;`)};
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const StyledArrow = styled(ChevronIcon)`
  color: ${({ theme }) => theme.colors.textBright};
  height: 16px;
  padding-left: 0.5rem;
  width: 16px;
`;

const StyledGridContainer = styled.div`
  margin: 1rem;
  padding-bottom: 1rem;

  @media ${breakpoints.medium} {
    margin: 1rem 2rem 2rem;
  }

  @media ${breakpoints.extraLarge} {
    margin: 1rem 3rem 3rem;
  }
`;

export const CreateMapsTab = () => {
  const dispatch = useDispatch();
  const {
    data: paginatedCustomMaps,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useGetCustomMapsInfiniteQuery(undefined);

  const customMaps = useMemo(() => paginatedCustomMaps?.pages.flat(), [paginatedCustomMaps?.pages]);

  const sharedTopics = useMemoisedSelector(getTopicsSharedWithMeIds, createTopicsSelector);
  const canAccessCreate = useSelector(getCanAccessCreate);
  const siteLanguage = useSelector(getSiteLanguage);
  const translate = useTranslate();

  const loadNextPage = async () => {
    if (hasNextPage && !isFetchingNextPage && !isFetching) await fetchNextPage();
  };

  useEffect(() => {
    if (!canAccessCreate) return;
    dispatch(fetchSharedWithMe({ limit: 8, offset: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessCreate, siteLanguage]);

  return (
    <>
      <CreateNewMapSection />
      {sharedTopics.length > 0 && (
        <StyledCarouselContainer>
          <TopicSection
            title={
              <StyledLink to="/shared">
                {translate("app.create.shared_with_me.title")}
                <StyledArrow />
              </StyledLink>
            }
            topicBack={topicBack}
            topics={sharedTopics.slice(0, 8)}
          />
        </StyledCarouselContainer>
      )}
      {customMaps && customMaps.length > 0 && (
        <InfinityLoader isPending={isFetching} loadNextPage={loadNextPage} hasNextPage={hasNextPage}>
          <StyledGridContainer>
            <SectionTitleUnderlined>{translate("app.create.my_maps.title")}</SectionTitleUnderlined>
            <StyledGrid numberOfItems={[1, 2, 3, 4, 5]}>
              {customMaps.map((map) => (
                <TopicTile
                  key={map.id}
                  hideOwnershipLabel
                  topicId={map.id}
                  topicName={map.name}
                  provider={map.provider ?? undefined}
                  link={`/create/${map.id}?editing=true`}
                  topicBack={topicBack}
                  attachment={map.attachment ?? undefined}
                  canEdit={map.can_edit ?? false}
                  isCustom={!!map.is_custom}
                  isFollowing={!!map.is_following}
                  isNew={!!map.is_new}
                  imageUrl={map.images?.x_small}
                  pageForTracking="Create landing"
                />
              ))}
            </StyledGrid>
          </StyledGridContainer>
        </InfinityLoader>
      )}
    </>
  );
};

const topicBack = { term: "app.create.title" } as const;
